import React, { useEffect, useState } from 'react';
import { Html5QrcodeScanner, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

function Scanner() {
    const navigate = useNavigate();
    const [scanResult, setScanResult] = useState(null);
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const isAuthenticated = !!(window as any).CURRENT_USER;

    useEffect(() => {
        // Initialize Scanner
        const supportedFormats = [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.UPC_EAN_EXTENSION,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
        ];

        const scanner = new Html5QrcodeScanner(
            "reader",
            {
                fps: 10,
                qrbox: { width: 250, height: 250 },
                aspectRatio: 1.0,
                formatsToSupport: supportedFormats,
                showTorchButtonIfSupported: true
            },
            /* verbose= */ false
        );

        const onScanSuccess = async (decodedText, decodedResult) => {
            console.log(`Code matched = ${decodedText}`, decodedResult);
            scanner.clear();
            setScanResult(decodedText);
            setIsProcessing(true);

            try {
                const url = new URL(decodedText);
                const path = url.pathname;

                // For authenticated users, try to get internal product ID
                if (isAuthenticated && path.startsWith('/p/')) {
                    const slug = path.split('/p/')[1];

                    try {
                        // Fetch product by slug to get internal ID
                        const response = await api.get(`/public/products/${slug}`);

                        if (response.product && response.product.id) {
                            // Redirect to internal product page
                            navigate(`/products/${response.product.id}`);
                            return;
                        }
                    } catch (err) {
                        console.error("Error fetching product:", err);
                    }

                    // Fallback to public page
                    navigate(path);
                } else if (path.startsWith('/units/') || path.startsWith('/product_units/')) {
                    const unitPath = path.replace('/units/', '/product_units/');
                    navigate(unitPath);
                } else if (path.startsWith('/products/')) {
                    navigate(path);
                } else {
                    window.location.href = decodedText;
                }
            } catch (e) {
                // If not a URL, treat as barcode/SKU and search
                console.log("Scanned text is not a URL, searching as product:", decodedText);
                navigate(`/products?search=${encodeURIComponent(decodedText)}`);
            }
        };

        const onScanFailure = (error) => {
            // Silently handle scan failures
        };

        scanner.render(onScanSuccess, onScanFailure);

        return () => {
            scanner.clear().catch(error => {
                console.error("Failed to clear html5-qrcode scanner. ", error);
            });
        };
    }, [navigate, isAuthenticated]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-slate-50 pb-24 lg:pb-8">
            <div className="max-w-2xl mx-auto px-4 pt-8">
                {/* Header */}
                <div className="text-center mb-8 animate-fade-in">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-4 shadow-lg shadow-indigo-200">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-slate-800 mb-2">Scan Code</h1>
                    <p className="text-slate-500">Point at a QR code or Barcode</p>
                </div>

                {/* Scanner Container */}
                <div className="bg-white p-6 rounded-3xl shadow-xl border border-slate-200/50 backdrop-blur-sm mb-6 animate-slide-up">
                    <div id="reader" className="w-full rounded-2xl overflow-hidden"></div>
                </div>

                {/* Success Message */}
                {scanResult && (
                    <div className="animate-slide-up p-6 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl text-center shadow-lg">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-500 rounded-full mb-3">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <p className="font-bold text-emerald-900 text-lg mb-2">Scanned Successfully!</p>
                        <p className="text-sm text-emerald-700 break-all mb-3 font-mono bg-white/50 px-3 py-2 rounded-lg">{scanResult}</p>
                        {isProcessing && (
                            <div className="flex items-center justify-center gap-2 text-emerald-600">
                                <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-sm font-medium">Loading product...</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="animate-slide-up p-6 bg-gradient-to-br from-rose-50 to-red-50 border border-rose-200 rounded-2xl text-center shadow-lg">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-rose-500 rounded-full mb-3">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <p className="font-bold text-rose-900 text-lg mb-2">Unable to Process</p>
                        <p className="text-sm text-rose-700 mb-4">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-6 py-3 bg-white border-2 border-rose-300 rounded-xl text-rose-700 font-bold hover:bg-rose-50 transition-all active:scale-95 shadow-sm"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                {/* Instructions */}
                {!scanResult && !error && (
                    <div className="text-center space-y-4 animate-fade-in">
                        <div className="flex items-center justify-center gap-8 text-slate-400">
                            <div className="flex flex-col items-center">
                                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-2">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </div>
                                <span className="text-xs font-medium">Position</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-2">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <span className="text-xs font-medium">Scan</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-2">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <span className="text-xs font-medium">View</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slide-up {
                    from { 
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in {
                    animation: fade-in 0.5s ease-out;
                }
                .animate-slide-up {
                    animation: slide-up 0.5s ease-out;
                }
            `}</style>
        </div>
    );
}

export default Scanner;
